import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { changeLanguage, changePrices } from "../../redux/environmentRedux"
// Components
import MobileDrawer from "../MobileDrawer/MobileDrawer"
import {
   Search,
   ShoppingBasketOutlined,
   AccountBoxOutlined,
   MenuOutlined,
   Visibility,
   VisibilityOff,
} from "@mui/icons-material"
import { Badge } from "@mui/material"
import logoWhite from "../../images/Logo_ivory_bg_transparent.png"
import logoBlack from "../../images/Logo_night_bg_transparent.png"
// import logoPink from "../../images/Logo_pink_bg_transparent.png"
// Styles
import {
   Container,
   Wrapper,
   Center,
   Left,
   Right,
   Language,
   SearchContainer,
   Input,
   Logo,
   MenuItem,
   StyledLink,
   MenuIconWrapper,
   MenuText,
} from "./Navbar.styles"

const Navbar = ({
   searched,
   getCartPosition,
   setCartPosition,
   bgImageColor,
}) => {
   const initial = useRef(true)
   const dispatch = useDispatch()
   const navigate = useNavigate()
   const { t, i18n } = useTranslation()
   const quantity = useSelector((state) => state.cart.totalQuantity)
   const user = useSelector((state) => state.user.currentUser)

   const defaultLanguage = useSelector((state) => state.environment.language)
   const [language, setLanguage] = useState(defaultLanguage)

   const [searchFocus, setSearchFocus] = useState(false)
   const [search, setSearch] = useState(searched)
   const [drawerOpen, setDrawerOpen] = useState(false)

   const defaultPrices = useSelector((state) => state.environment.prices)
   const [displayPrices, setDisplayPrices] = useState(defaultPrices)

   const handleLanguage = (e, selected) => {
      e.preventDefault()
      setLanguage(selected)
      dispatch(changeLanguage(selected))
      i18n.changeLanguage(selected)
   }

   const togglePrices = () => {
      dispatch(changePrices(!displayPrices))
      setDisplayPrices((prev) => !prev)
   }

   const toggleDrawer = () => setDrawerOpen((prev) => !prev)

   useEffect(() => {
      if (initial.current || !search || search.trim() === "") {
         initial.current = false
         return
      }
      const timer = setTimeout(() => {
         navigate(`/search/${search}`)
      }, 1000)
      return () => clearTimeout(timer)
   }, [search, navigate])

   useEffect(() => {
      getCartPosition &&
         setCartPosition({
            top: document.getElementById("cartIcon")?.offsetTop,
            left: document.getElementById("cartIcon")?.offsetLeft,
         })
   }, [getCartPosition, setCartPosition])

   useEffect(() => {
      if (user?.type === "public" || !user) {
         dispatch(changePrices(true))
         setDisplayPrices(true)
      }
   }, [user, dispatch])

   return (
      <Container>
         <Wrapper
            textColor={bgImageColor === "dark" ? "lightGrey" : "darkGrey"}
         >
            <MenuIconWrapper onClick={toggleDrawer}>
               <MenuOutlined />
            </MenuIconWrapper>
            <Left>
               <StyledLink to={"/products/optical"}>
                  <MenuItem>{t("navbar.menu.m1")}</MenuItem>
               </StyledLink>
               <StyledLink to={"/products/sun"}>
                  <MenuItem>{t("navbar.menu.m2")}</MenuItem>
               </StyledLink>
               <StyledLink to={"/products/accessory"}>
                  <MenuItem>{t("navbar.menu.m7")}</MenuItem>
               </StyledLink>
               <StyledLink to={"/story"}>
                  <MenuItem>{t("navbar.menu.m3")}</MenuItem>
               </StyledLink>
               <StyledLink to={"/mif"}>
                  <MenuItem>{t("navbar.menu.m4")}</MenuItem>
               </StyledLink>
               <StyledLink to={"/new-collection"}>
                  <MenuItem>{t("navbar.menu.m6")}</MenuItem>
               </StyledLink>
               <StyledLink to={"/shopfinder"}>
                  <MenuItem>{t("navbar.menu.m5")}</MenuItem>
               </StyledLink>
            </Left>
            <Center searchFocus={searchFocus}>
               <StyledLink to="/">
                  <Logo src={bgImageColor === "dark" ? logoWhite : logoBlack} />
               </StyledLink>
            </Center>
            <Right>
               <SearchContainer
                  textColor={bgImageColor === "dark" ? "lightGrey" : "darkGrey"}
               >
                  <Input
                     placeholder={searched ? searched : t("navbar.input.i1")}
                     onFocus={() => setSearchFocus(true)}
                     onBlur={() => setSearchFocus(false)}
                     onChange={(e) => setSearch(e.target.value)}
                     textColor={
                        bgImageColor === "dark" ? "lightGrey" : "darkGrey"
                     }
                  />
                  <Search style={{ cursor: "pointer" }} />
               </SearchContainer>
               <>
                  <StyledLink
                     to={user ? "/account" : "/login"}
                     displayMobile="none"
                  >
                     <MenuItem id="loginIcon">
                        <AccountBoxOutlined color="text" fontSize="medium" />
                     </MenuItem>
                  </StyledLink>
                  {user?.type !== "public" && user ? (
                     <StyledLink to="/account/sav" displayMobile="none">
                        <MenuText>{t("navbar.menu.m8")}</MenuText>
                     </StyledLink>
                  ) : (
                     <></>
                  )}
                  <StyledLink to="/cart">
                     <MenuItem id="cartIcon">
                        <Badge badgeContent={quantity} color="secondary">
                           <ShoppingBasketOutlined
                              color="text"
                              fontSize="medium"
                           />
                        </Badge>
                     </MenuItem>
                  </StyledLink>
                  {user?.type !== "public" && user ? (
                     <MenuItem id="viewIcon">
                        {displayPrices ? (
                           <Visibility
                              color="text"
                              fontSize="medium"
                              onClick={togglePrices}
                           />
                        ) : (
                           <VisibilityOff
                              color="text"
                              fontSize="medium"
                              onClick={togglePrices}
                           />
                        )}
                     </MenuItem>
                  ) : (
                     <></>
                  )}
                  <Language
                     onClick={(e) => handleLanguage(e, "en")}
                     selected={language === "en"}
                  >
                     EN
                  </Language>
                  <Language
                     onClick={(e) => handleLanguage(e, "fr")}
                     selected={language === "fr"}
                  >
                     FR
                  </Language>
               </>
            </Right>
         </Wrapper>
         <MobileDrawer open={drawerOpen} onClose={toggleDrawer} />
      </Container>
   )
}

Navbar.propTypes = {
   searched: PropTypes.string,
   getCartPosition: PropTypes.bool,
   setCartPosition: PropTypes.func,
   bgImageColor: PropTypes.string,
}

export default Navbar
